import React from 'react';
import type { ShopifyCart, CalculationResult } from '../types';
import { formatCurrency } from '../utils/formatCurrency';

interface StickyCheckoutProps {
  shopifyCart: ShopifyCart | null;
  calculationResult: CalculationResult | null;
  calculating: boolean;
  creatingCheckout?: boolean;
  onCheckout?: () => void;
}

export const StickyCheckout: React.FC<StickyCheckoutProps> = ({
  shopifyCart,
  calculationResult,
  calculating,
  creatingCheckout = false,
  onCheckout,
}) => {
  if (!shopifyCart || shopifyCart.items.length === 0) {
    return null;
  }

  const originalTotal = shopifyCart.total_price / 100; // Convert cents
  const totalDiscount = calculationResult?.total_discount || 0;
  const finalTotal = calculationResult?.final_total ?? originalTotal;
  const hasFreeShipping = Boolean(calculationResult?.shipping_discount?.eligible);

  const handleCheckout = () => {
    if (onCheckout) {
      onCheckout();
    } else {
      // Default: redirect to Shopify checkout
      window.location.href = '/checkout';
    }
  };

  return (
    <>
      <style>{`
        @media (max-width: 768px) {
          .intan-sticky-checkout {
            flex-direction: column !important;
            gap: 12px !important;
            padding: 14px 16px !important;
          }
          .intan-sticky-checkout .checkout-summary {
            width: 100% !important;
            margin-bottom: 0 !important;
          }
          .intan-sticky-checkout .checkout-button {
            width: 100% !important;
            min-width: unset !important;
            padding: 16px 20px !important;
            font-size: 17px !important;
          }
          .intan-sticky-checkout .total-row {
            flex-wrap: wrap !important;
            gap: 8px !important;
          }
          .intan-sticky-checkout .total-row > div:first-child {
            font-size: 14px !important;
          }
          .intan-sticky-checkout .total-row span {
            font-size: 14px !important;
          }
          .intan-sticky-checkout .total-row span:last-child {
            font-size: 19px !important;
          }
          .intan-sticky-checkout .savings-badge {
            width: 100% !important;
            justify-content: center !important;
            padding: 10px 12px !important;
          }
          .intan-sticky-checkout .savings-badge span {
            font-size: 13px !important;
          }
        }
        @media (min-width: 769px) {
          .intan-sticky-checkout {
            flex-direction: row !important;
            padding: 16px 24px !important;
            gap: 24px !important;
            align-items: center !important;
          }
          .intan-sticky-checkout .checkout-summary {
            width: auto !important;
            flex: 1 !important;
          }
          .intan-sticky-checkout .checkout-button {
            width: auto !important;
            min-width: 200px !important;
            padding: 16px 40px !important;
            font-size: 16px !important;
          }
          .intan-sticky-checkout .total-row {
            flex-wrap: nowrap !important;
            gap: 12px !important;
            margin-bottom: 6px !important;
          }
          .intan-sticky-checkout .total-row > div:first-child {
            font-size: 14px !important;
          }
          .intan-sticky-checkout .total-row span {
            font-size: 14px !important;
          }
          .intan-sticky-checkout .total-row span:last-child {
            font-size: 20px !important;
          }
          .intan-sticky-checkout .savings-badge {
            width: fit-content !important;
            padding: 6px 12px !important;
          }
          .intan-sticky-checkout .savings-badge span {
            font-size: 13px !important;
          }
        }
      `}</style>
      <div
        className="intan-sticky-checkout"
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: '#fff',
          borderTop: '2px solid #e0e0e0',
          boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
          padding: '12px 16px',
          zIndex: 999999,
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        {/* Summary with Amount Saved */}
        <div className="checkout-summary" style={{ width: '100%', flex: 1, minWidth: 0 }}>
          {totalDiscount > 0 ? (
            <div>
              <div
                className="total-row"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flexWrap: 'wrap'
                }}
              >
                <div style={{ 
                  fontSize: '13px', 
                  color: '#666',
                  fontWeight: '500'
                }}>
                  Total:
                </div>
                <span style={{ 
                  textDecoration: 'line-through',
                  color: '#999',
                  fontSize: '13px'
                }}>
                  {formatCurrency(originalTotal)}
                </span>
                <span style={{ 
                  color: '#28a745',
                  fontWeight: '700',
                  fontSize: '18px'
                }}>
                  {formatCurrency(finalTotal)}
                </span>
              </div>
            </div>
          ) : (
            <div style={{ 
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexWrap: 'wrap'
            }}>
              <div style={{ 
                fontSize: '13px', 
                color: '#666',
                fontWeight: '500'
              }}>
                Total:
              </div>
              <div style={{ 
                fontSize: '18px',
                fontWeight: '700',
                color: '#333'
              }}>
                {formatCurrency(originalTotal)}
              </div>
            </div>
          )}
        </div>

        {/* Checkout Button */}
        <button
          className="checkout-button"
          onClick={handleCheckout}
          disabled={calculating || creatingCheckout || shopifyCart.items.length === 0}
          style={{
            width: '100%',
            padding: '11px 16px',
            backgroundColor: '#6F1920',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            fontSize: '15px',
            fontWeight: '700',
            cursor: calculating || shopifyCart.items.length === 0 ? 'not-allowed' : 'pointer',
            opacity: calculating || shopifyCart.items.length === 0 ? 0.6 : 1,
            transition: 'opacity 0.2s, background-color 0.2s, transform 0.1s',
            boxShadow: '0 2px 6px rgba(111, 25, 32, 0.28)',
            touchAction: 'manipulation',
            WebkitTapHighlightColor: 'transparent',
            minHeight: '44px',
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
          onMouseEnter={(e) => {
            if (!calculating && shopifyCart.items.length > 0) {
              e.currentTarget.style.backgroundColor = '#59141A';
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 3px 10px rgba(111, 25, 32, 0.38)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#6F1920';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 6px rgba(111, 25, 32, 0.28)';
          }}
          onMouseDown={(e) => {
            if (!calculating && shopifyCart.items.length > 0) {
              e.currentTarget.style.transform = 'translateY(0)';
            }
          }}
          onMouseUp={(e) => {
            if (!calculating && shopifyCart.items.length > 0) {
              e.currentTarget.style.transform = 'translateY(-1px)';
            }
          }}
          onTouchStart={(e) => {
            if (!calculating && shopifyCart.items.length > 0) {
              e.currentTarget.style.backgroundColor = '#59141A';
              e.currentTarget.style.opacity = '0.9';
            }
          }}
          onTouchEnd={(e) => {
            e.currentTarget.style.backgroundColor = '#6F1920';
            e.currentTarget.style.opacity = (calculating || creatingCheckout || shopifyCart.items.length === 0) ? '0.6' : '1';
          }}
        >
          {creatingCheckout
            ? 'Processing checkout...'
            : calculating
              ? 'Calculating...'
              : (
            totalDiscount > 0 ? (
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                flexWrap: 'wrap',
                justifyContent: 'center'
              }}>
                <span style={{
                  width: '0',
                  height: '0',
                  borderLeft: '6px solid transparent',
                  borderRight: '6px solid transparent',
                  borderTop: '8px solid #fff',
                  opacity: 0.9
                }} />
                <span style={{ fontSize: '15px', fontWeight: 700 }}>
                  Checkout now & save {formatCurrency(totalDiscount)}
                </span>
              </span>
            ) : hasFreeShipping ? (
              <span>Checkout with free shipping</span>
            ) : (
              <span>Proceed to Checkout</span>
            )
          )
          }
        </button>
      </div>
    </>
  );
};

